import React, { PureComponent } from 'react';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { Button, Card, Divider, Grid, Icon } from 'semantic-ui-react';
import { withRouter } from 'react-router';
import { DyzioSecondaryButton } from 'dyzio-shared-components/lib/components/dyzio-buttons';
import hasRESTClient from '../../hocs/has-rest-client';
import hasUserInfo from '../../hocs/has-user-info';
import VerticalInfluencerBar from '../../components/vertical-influencer-bar';

import InstagramPost from './instagram-post';
import InstagramStory, { STORY_TYPE } from './instagram-story';
import ADSModal from './ads-modal';
import ExistingAds from './existing-ads';

const backButtonStyle = {
  border: '0px solid #1d1937',
};

const adsLinkStyle = {
  post: {
    width: '200px',
  },
  story: {
    width: '400px',
  },
};

class ViewAssetPage extends PureComponent {
  //
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      isLoadingAds: true,
      processedAdId: null,
      adsModalOpen: false,
      failedToLoad: false,
      failedToLoadAds: false,
      asset: null,
      ads: [],
      campaign: null,
    };
  }

  async componentDidMount() {
    const { routeParams, restClient, buildRESTClient } = this.props;
    const assetId = _.get(routeParams, 'assetId');
    const assetType = _.get(routeParams, 'assetType');
    const resourceURL = _.eq(assetType, 'story') ? 'stories' : 'trackedAssets';
    const response = await restClient.get(`${resourceURL}/${assetId}?include=campaign,discussion,discussion.organisations.influencer`).catch(() => {
      this.setState({
        isLoading: false,
        failedToLoad: true,
      });
    });
    if (response) {
      const data = _.get(response, 'data.data');
      const included = _.get(response, 'data.included');
      const campaign = _.find(included, { type: 'campaigns' });
      const influencer = _.find(included, { type: 'influencer' });
      this.setState({
        asset: data,
        isLoading: false,
        failedToLoad: false,
        campaign,
        influencer,
      });
      const shareAPIClient = buildRESTClient({
        baseURL: process.env.REACT_APP_REPORT_API_URL,
        hasToken: true,
      });
      const adsResponse = await shareAPIClient.get(`/marketing/campaign/by-id/${campaign.id}`).catch(() => {
        this.setState({
          failedToLoadAds: true,
        });
      });
      const ads = _.get(adsResponse, 'data.data', []);
      this.setState({
        ads,
        isLoadingAds: false,
        failedToLoadAds: _.has(adsResponse, 'data.errorType'),
      });
    }
  }

  returnToCollaboration = () => {
    const { history } = this.props;
    history.goBack();
  }

  toggleAdsModalState = () => {
    this.setState({
      adsModalOpen: !this.state.adsModalOpen,
    });
  }

  updateAssetLinkedAds = async ({
    assetType,
    assetId,
    linkedAds,
  }) => {
    const { restClient } = this.props;
    const updatedAssetResponse = await restClient.patch(`${assetType}/${assetId}`, {
      data: {
        attributes: {
          linkedAds,
        },
      },
    });
    this.setState({
      asset: _.get(updatedAssetResponse, 'data.data'),
      processedAdId: null,
    });
  }

  unlinkAd = async adId => {
    const { asset } = this.state;
    this.setState({
      processedAdId: adId,
    });
    const linkedAds = _.get(asset, 'attributes.linkedAds', []);
    const updatedAds = _.reject(linkedAds, ['id', adId]);
    const assetType = _.get(asset, 'type');
    const assetId = _.get(asset, 'id');
    await this.updateAssetLinkedAds({
      assetType,
      assetId,
      linkedAds: updatedAds,
    });
  }

  linkAd = async adId => {
    this.setState({
      processedAdId: adId,
    });
    const { ads, asset } = this.state;
    const adToLink = _.find(_.get(ads, 'ads', []), ['ad.id', adId]);
    const fields = {
      id: adId,
      objectType: _.get(adToLink, 'ad.creative.object_type'),
      name: _.get(adToLink, 'ad.campaign.name'),
      campaignName: _.get(adToLink, 'ad.campaign.name'),
      adName: _.get(adToLink, 'ad.name'),
      thumbnail: _.get(adToLink, 'ad.creative.thumbnail_url'),
    };
    const existingAds = _.get(asset, 'attributes.linkedAds', []);
    existingAds.push(fields);
    const assetType = _.get(asset, 'type');
    const assetId = _.get(asset, 'id');
    await this.updateAssetLinkedAds({
      assetType,
      assetId,
      linkedAds: existingAds,
    });
  }

  render() {
    const {
      asset,
      campaign,
      influencer,
      isLoading,
      adsModalOpen,
      ads,
      processedAdId,
      failedToLoadAds,
    } = this.state;
    const existingAds = _.get(asset, 'attributes.linkedAds', []);
    const isInstagramPost = _.get(asset, 'attributes.platform') === 'INSTAGRAM' && _.get(asset, 'type') !== STORY_TYPE;
    const isInstagramStory = _.get(asset, 'type') === STORY_TYPE;
    const verticalBarProps = _.get(influencer, 'attributes', {});
    return (
      <div className="page">
        <div className="page-content">
          <Grid>
            <Grid.Row>
              <Grid.Column width="4">
                <Card fluid>
                  <Card.Content>
                    <DyzioSecondaryButton
                      label="back to list"
                      icon={<Icon name="arrow left" />}
                      style={backButtonStyle}
                      hasIcon
                      onClick={this.returnToCollaboration}
                      fluid
                    />
                  </Card.Content>
                </Card>
                <VerticalInfluencerBar {...verticalBarProps} isLoading={isLoading} />
                <Card fluid>
                  <Card.Content>
                    <Button fluid inverted color="blue" content="Link Facebook Ads" onClick={this.toggleAdsModalState} />
                  </Card.Content>
                </Card>
              </Grid.Column>

              <Grid.Column width="12">
                {
                  isInstagramPost && <InstagramPost
                    asset={asset}
                    influencer={influencer}
                    campaign={campaign}
                    ads={existingAds}
                    unlinkAd={this.unlinkAd}
                  />
                }
                {
                  isInstagramStory && <InstagramStory
                    story={asset}
                    ads={existingAds}
                    unlinkAd={this.unlinkAd}
                  />
                }
              </Grid.Column>

            </Grid.Row>
          </Grid>
        </div>
        {
          adsModalOpen && <ADSModal
            linkAd={this.linkAd}
            onClose={this.toggleAdsModalState}
            ads={ads}
            existingAds={existingAds}
            processedAdId={processedAdId}
            failedToLoadAds={failedToLoadAds}
          />
        }
      </div>
    );
  }
}

ViewAssetPage.propTypes = {
  history: PropTypes.object.isRequired,
};

export default compose(
  hasRESTClient,
  hasUserInfo,
  withRouter,
)(ViewAssetPage);



/** WEBPACK FOOTER **
 ** ./src/containers/view-asset-page/index.js
 **/